<template>
  <div class="face-recognition-container">
    <div class="title">请将人脸对准屏幕摄像头</div>

    <!-- 视频容器 -->
    <div class="video-wrapper">
      <video ref="video" autoplay playsinline></video>
      <div class="detection-box"></div>
    </div>

    <!-- 操作按钮 -->
    <div class="action-buttons">
      <el-button
        :loading="loading"
        :disabled="!isFaceInArea"
        @click="capture"
        class="capture-btn"
      >
        {{ isFaceInArea ? "立即拍照" : "请调整面部位置" }}
      </el-button>
    </div>
  </div>
</template>

<script>
import * as faceapi from "face-api.js";
import { uploadFile, examPhotoAdd } from "@/api/system";
import to from "await-to";
export default {
  name: "FaceRecognition",
  data() {
    return {
      loading: false,
      isFaceInArea: false,
      mediaStream: null,
      detectionInterval: null,
    };
  },
  mounted() {
    this.loadModels();
    this.initMedia();
  },
  beforeDestroy() {
    this.closeModal();
  },
  methods: {
    async loadModels() {
      try {
        await faceapi.nets.tinyFaceDetector.loadFromUri("/models");
      } catch (error) {
        console.error("模型加载失败:", error);
      }
    },

    async initMedia() {
      try {
        this.mediaStream = await navigator.mediaDevices.getUserMedia({
          video: { width: 768, height: 432 },
        });
        this.$refs.video.srcObject = this.mediaStream;
        this.startDetection();
      } catch (error) {
        console.error("摄像头错误:", error);
        alert("无法访问摄像头，请检查权限设置");
      }
    },

    closeModal() {
      this.stopDetection();
      if (this.mediaStream) {
        this.mediaStream.getTracks().forEach((track) => track.stop());
      }
    },

    startDetection() {
      this.detectionInterval = setInterval(async () => {
        const video = this.$refs.video;
        if (!video || video.readyState < 2) return;

        // 获取视频尺寸
        const videoWidth = video.videoWidth;
        const videoHeight = video.videoHeight;

        // 计算检测区域（中间偏上）
        const box = {
          width: videoWidth * 0.5,
          height: videoHeight * 0.6,
          x: videoWidth * 0.25,
          y: videoHeight * 0.15,
        };

        // 人脸检测
        const detections = await faceapi.detectAllFaces(
          video,
          new faceapi.TinyFaceDetectorOptions()
        );

        // 检查是否有人脸在区域内
        this.isFaceInArea = detections.some((detection) => {
          const faceCenter = {
            x: detection.box.x + detection.box.width / 2,
            y: detection.box.y + detection.box.height / 2,
          };

          return (
            faceCenter.x > box.x &&
            faceCenter.x < box.x + box.width &&
            faceCenter.y > box.y &&
            faceCenter.y < box.y + box.height
          );
        });
      }, 500);
    },

    stopDetection() {
      clearInterval(this.detectionInterval);
    },

    capture() {
      this.loading = true;
      try {
        const video = this.$refs.video;
        const canvas = document.createElement("canvas");
        const ctx = canvas.getContext("2d");

        // 设置画布尺寸
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;

        // 绘制当前帧
        ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

        // 转换为文件并上传
        canvas.toBlob(
          (blob) => {
            const file = new File([blob], "face-photo.jpg", {
              type: "image/jpeg",
              lastModified: Date.now(),
            });
            this.uploadFile(file);
          },
          "image/jpeg",
          0.9
        );
      } catch (error) {
        console.log(`output->error`, error);
        this.loading = false;
      }
    },

    async uploadFile(file) {
      const formData = new FormData();
      formData.append("file", file);

      const [res, err] = await to(uploadFile(formData));
      setTimeout(() => {
        this.loading = false;
      }, 1000);
      if (err) return this.$message.warning(err.msg);
      this.addPhoto(res.data);
    },
    async addPhoto(url) {
      const [, err] = await to(
        examPhotoAdd({
          examSubjectId: this.$route.query.examSubjectId,
          planId: this.$route.query.examPlanId,
          url,
          photoType: 1,
        })
      );
      if (err) return this.$message.warning(err.msg);

      this.$router.push({
        path: "/exam/testPaper",
        query: {
          examSubjectId: this.$route.query.examSubjectId,
          examPlanId: this.$route.query.examPlanId,
          t: this.$route.query.t,
        },
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.video-wrapper {
  position: relative;
  width: 768px;
  height: 432px;
  margin: 0 auto;
  border-radius: 6px;
  overflow: hidden;
}

video {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.detection-box {
  position: absolute;
  width: 50%;
  height: 66.6%;
  left: 25%;
  top: 10%;
  border: 3px solid #00ff88;
  border-radius: 12px;
  box-shadow: 0 0 15px rgba(0, 255, 136, 0.4);

  /* 辅助瞄准线 */
  &::before,
  &::after {
    content: "";
    position: absolute;
    background: rgba(255, 255, 255, 0.3);
  }

  &::before {
    /* 水平线 */
    top: 50%;
    left: 10%;
    right: 10%;
    height: 1px;
    transform: translateY(-1px);
  }

  &::after {
    /* 垂直线 */
    left: 50%;
    top: 20%;
    bottom: 20%;
    width: 1px;
    transform: translateX(-1px);
  }
}
.action-buttons {
  text-align: center;
}
.capture-btn {
  width: 150px;
  height: 40px;
  line-height: 40px;
  margin-top: 10px;
  background: #00c853;
  color: white;
  border: none;
  font-size: 16px;
  cursor: pointer;
  padding: 0;
  transition: all 0.3s;
  &:disabled {
    background: #9e9e9e;
    cursor: not-allowed;
  }
  &:not(:disabled):hover {
    background: #009624;
    transform: scale(1.05);
  }
}

.title {
  margin: 0;
  color: #333;
  text-align: center;
  color: #67c23a;
  font-size: 14px;
  margin-bottom: 10px;
}
</style>
